import React from 'react';
import { Button, Modal, notification } from 'tea-component';
import { YAML_TEST_FAILURE_NOTIFICATION_CLASS } from '../constant';
import { normalizeDatalabRequestError } from './normalizeDatalabRequestError';

const SHORT_MAX_LINES = 3;
const SHORT_MAX_CHARS = 320;
const LONG_SUMMARY_CHARS = 160;

/** 同一时间只保留一个失败详情 Modal，避免重复点「查看详情」叠多层 */
let activeYamlTestDetailModal: { destroy: () => void; } | null = null;

export type TYamlTestFormatMessage = (
  descriptor: { id: string; defaultMessage?: string; },
  values?: Record<string, unknown>,
) => string;

/** i18n 描述符或直接文案，便于各场景复用同一套通知 UI */
export type TYamlFeedbackMsg =
  | string
  | {
    id: string;
    defaultMessage?: string;
    values?: Record<string, unknown>;
  };

export interface IYamlTestFailureItem {
  name?: string;
  msg?: string;
  code?: string | number;
  uuid?: string;
}

export type TShowYamlTestFailureInput =
  | { kind: 'single'; msg?: string; code?: string | number; }
  | { kind: 'batch'; failures: IYamlTestFailureItem[]; }
  | { kind: 'request'; err: unknown; };

/** 可覆盖的文案项（未传则走 YAML 测试默认 id） */
export type TShowYamlTestFailureCopy = {
  title?: TYamlFeedbackMsg;
  fallbackMsg?: TYamlFeedbackMsg;
  detailTitle?: TYamlFeedbackMsg;
  batchLongSummary?: TYamlFeedbackMsg;
  unnamedModel?: TYamlFeedbackMsg;
  errorCode?: TYamlFeedbackMsg;
  viewDetail?: TYamlFeedbackMsg;
  copy?: TYamlFeedbackMsg;
  copySuccess?: TYamlFeedbackMsg;
  copyFailed?: TYamlFeedbackMsg;
  close?: TYamlFeedbackMsg;
};

/** 兼容旧版 `*MsgId` 字段，新代码请用 `TShowYamlTestFailureCopy` */
export type TShowYamlTestFailureOptions = TShowYamlTestFailureCopy & {
  /** @deprecated 使用 fallbackMsg: { id } */
  fallbackMsgId?: string;
  /** @deprecated 使用 detailTitle: { id } */
  detailTitleId?: string;
  /** @deprecated 使用 batchLongSummary: { id } */
  batchLongSummaryId?: string;
};

const DEFAULT_COPY = {
  title: { id: 'dbl_model_management_test_failed' },
  fallbackMsg: { id: 'dbl_model_management_test_failed' },
  detailTitle: { id: 'dbl_model_management_yaml_test_detail_title' },
  batchLongSummary: {
    id: 'dbl_model_management_yaml_test_batch_long_summary',
  },
  unnamedModel: { id: 'dbl_model_management_yaml_test_unnamed_model' },
  errorCode: { id: 'dbl_model_management_yaml_test_error_code' },
  viewDetail: { id: 'dbl_model_management_yaml_test_view_detail' },
  copy: { id: 'dbl_model_management_yaml_test_copy' },
  copySuccess: { id: 'dbl_model_management_yaml_test_copy_success' },
  copyFailed: { id: 'dbl_model_management_yaml_test_copy_failed' },
  close: { id: 'dbl_common_close' },
} as const satisfies Record<keyof TShowYamlTestFailureCopy, TYamlFeedbackMsg>;

type TResolvedCopy = Record<keyof TShowYamlTestFailureCopy, TYamlFeedbackMsg>;

/** 将 TYamlFeedbackMsg 解析为展示字符串 */
export const resolveYamlFeedbackMsg = (
  formatMessage: TYamlTestFormatMessage,
  msg: TYamlFeedbackMsg,
  mergeValues?: Record<string, unknown>,
): string => {
  if (typeof msg === 'string') {
    return msg;
  }
  return formatMessage(
    { id: msg.id, defaultMessage: msg.defaultMessage },
    { ...mergeValues, ...msg.values },
  );
};

const resolveCopyField = (
  formatMessage: TYamlTestFormatMessage,
  copy: TResolvedCopy,
  key: keyof TShowYamlTestFailureCopy,
  mergeValues?: Record<string, unknown>,
): string =>
  resolveYamlFeedbackMsg(formatMessage, copy[key], mergeValues);

const normalizeOptions = (
  options?: TShowYamlTestFailureOptions,
): TResolvedCopy => {
  const merged: TShowYamlTestFailureCopy = { ...options };
  if (options?.fallbackMsgId && !merged.fallbackMsg) {
    merged.fallbackMsg = { id: options.fallbackMsgId };
  }
  if (options?.detailTitleId && !merged.detailTitle) {
    merged.detailTitle = { id: options.detailTitleId };
  }
  if (options?.batchLongSummaryId && !merged.batchLongSummary) {
    merged.batchLongSummary = { id: options.batchLongSummaryId };
  }
  return {
    title: merged.title ?? DEFAULT_COPY.title,
    fallbackMsg: merged.fallbackMsg ?? DEFAULT_COPY.fallbackMsg,
    detailTitle: merged.detailTitle ?? DEFAULT_COPY.detailTitle,
    batchLongSummary: merged.batchLongSummary ?? DEFAULT_COPY.batchLongSummary,
    unnamedModel: merged.unnamedModel ?? DEFAULT_COPY.unnamedModel,
    errorCode: merged.errorCode ?? DEFAULT_COPY.errorCode,
    viewDetail: merged.viewDetail ?? DEFAULT_COPY.viewDetail,
    copy: merged.copy ?? DEFAULT_COPY.copy,
    copySuccess: merged.copySuccess ?? DEFAULT_COPY.copySuccess,
    copyFailed: merged.copyFailed ?? DEFAULT_COPY.copyFailed,
    close: merged.close ?? DEFAULT_COPY.close,
  };
};

function isShortText(text: string): boolean {
  if (!text) {
    return true;
  }
  const lines = text.split(/\r?\n/);
  return lines.length <= SHORT_MAX_LINES && text.length <= SHORT_MAX_CHARS;
}

function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) {
    return text;
  }
  return `${text.slice(0, maxChars)}…`;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function formatErrorCodeLine(
  formatMessage: TYamlTestFormatMessage,
  copy: TResolvedCopy,
  code?: string | number,
): string {
  if (code === undefined || code === null || code === '') {
    return '';
  }
  return resolveCopyField(formatMessage, copy, 'errorCode', {
    code: String(code),
  });
}

function buildSingleFullText(
  formatMessage: TYamlTestFormatMessage,
  copy: TResolvedCopy,
  msg?: string,
  code?: string | number,
): string {
  const fallback = resolveCopyField(formatMessage, copy, 'fallbackMsg');
  const body = (msg && String(msg).trim()) || fallback;
  const codeLine = formatErrorCodeLine(formatMessage, copy, code);
  return codeLine ? `${body}\n${codeLine}` : body;
}

/** 行内失败详情文案（与通知/弹窗全文口径一致；默认测试失败文案） */
export function formatYamlTestFailuresText(
  formatMessage: TYamlTestFormatMessage,
  failures: IYamlTestFailureItem[],
  copy?: Pick<TShowYamlTestFailureCopy, 'fallbackMsg' | 'unnamedModel' | 'errorCode'>,
): string {
  const resolved = normalizeOptions(copy);
  if (!failures.length) {
    return '';
  }
  if (failures.length === 1) {
    const f = failures[0];
    return buildSingleFullText(formatMessage, resolved, f.msg, f.code);
  }
  return buildBatchFullText(formatMessage, resolved, failures);
}

function buildBatchFullText(
  formatMessage: TYamlTestFormatMessage,
  copy: TResolvedCopy,
  failures: IYamlTestFailureItem[],
): string {
  const unnamed = resolveCopyField(formatMessage, copy, 'unnamedModel');
  const fallback = resolveCopyField(formatMessage, copy, 'fallbackMsg');
  return failures
    .map((f, idx) => {
      const title = f.name || f.uuid || `${unnamed} #${idx + 1}`;
      const body = (f.msg && String(f.msg).trim()) || fallback;
      const codeLine = formatErrorCodeLine(formatMessage, copy, f.code);
      const block = codeLine ? `${body}\n${codeLine}` : body;
      return `${title}\n${block}`;
    })
    .join('\n\n');
}

function openYamlTestFailureDetailModal(
  formatMessage: TYamlTestFormatMessage,
  copy: TResolvedCopy,
  fullText: string,
): void {
  if (activeYamlTestDetailModal) {
    activeYamlTestDetailModal.destroy();
    activeYamlTestDetailModal = null;
  }

  let detailModalDestroyed = false;
  const closeDetailModal = (): void => {
    if (detailModalDestroyed) {
      return;
    }
    detailModalDestroyed = true;
    if (activeYamlTestDetailModal === modal) {
      activeYamlTestDetailModal = null;
    }
    modal.destroy();
  };
  const modal = Modal.show({
    size: 'l',
    destroyOnClose: true,
    caption: resolveCopyField(formatMessage, copy, 'detailTitle'),
    onClose: () => {
      closeDetailModal();
    },
    children: (
      <>
        <Modal.Body>
          <pre
            style={{
              maxHeight: 420,
              overflow: 'auto',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {fullText}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={() => {
              void (async () => {
                const showCopyFailed = (): void => {
                  notification.error({
                    description: resolveCopyField(
                      formatMessage,
                      copy,
                      'copyFailed',
                    ),
                  });
                };
                try {
                  const ok = await copyTextToClipboard(fullText);
                  if (ok) {
                    notification.success({
                      description: resolveCopyField(
                        formatMessage,
                        copy,
                        'copySuccess',
                      ),
                    });
                  } else {
                    showCopyFailed();
                  }
                } catch {
                  showCopyFailed();
                }
              })();
            }}
          >
            {resolveCopyField(formatMessage, copy, 'copy')}
          </Button>
          <Button type="weak" onClick={() => closeDetailModal()}>
            {resolveCopyField(formatMessage, copy, 'close')}
          </Button>
        </Modal.Footer>
      </>
    ),
  });
  activeYamlTestDetailModal = modal;
}

/**
 * 展示失败原因：短文案直接进通知，长文案摘要 + footer「查看详情」打开全文弹窗。
 * `copy` 可传入任意 i18n id（见 TShowYamlTestFailureCopy），未传则用测试失败默认文案。
 */
export function showYamlTestFailureFeedback(
  formatMessage: TYamlTestFormatMessage,
  input: TShowYamlTestFailureInput,
  options?: TShowYamlTestFailureOptions,
): void {
  const copy = normalizeOptions(options);
  const notificationTitle = resolveCopyField(formatMessage, copy, 'title');

  let fullText: string;
  let notificationDescription: React.ReactNode;
  let short = false;
  const fallback = resolveCopyField(formatMessage, copy, 'fallbackMsg');

  if (input.kind === 'request') {
    const { msg, code } = normalizeDatalabRequestError(input.err);
    fullText = buildSingleFullText(formatMessage, copy, msg, code);
    short = isShortText(fullText);
    notificationDescription = short
      ? fullText
      : truncateText(fullText, LONG_SUMMARY_CHARS);
  } else if (input.kind === 'single') {
    fullText = buildSingleFullText(
      formatMessage,
      copy,
      input.msg,
      input.code,
    );
    short = isShortText(fullText);
    if (short) {
      notificationDescription = fullText;
    } else {
      const summary = truncateText(
        (input.msg && String(input.msg).trim()) || fallback,
        LONG_SUMMARY_CHARS,
      );
      const codeLine = formatErrorCodeLine(
        formatMessage,
        copy,
        input.code,
      );
      notificationDescription = codeLine
        ? `${summary}\n${codeLine}`
        : summary;
    }
  } else {
    const failures = input.failures || [];
    if (!failures.length) {
      fullText = fallback;
      short = true;
      notificationDescription = fullText;
    } else {
      fullText = buildBatchFullText(formatMessage, copy, failures);
      short = isShortText(fullText);
      if (short) {
        notificationDescription = fullText;
      } else {
        notificationDescription = resolveCopyField(
          formatMessage,
          copy,
          'batchLongSummary',
          {
            count: failures.length,
            preview: truncateText(fullText, LONG_SUMMARY_CHARS),
          },
        );
      }
    }
  }

  const viewDetailLabel = resolveCopyField(formatMessage, copy, 'viewDetail');

  notification.error({
    className: YAML_TEST_FAILURE_NOTIFICATION_CLASS,
    title: notificationTitle,
    description: notificationDescription,
    footer: viewDetailLabel,
    onFooterClick: () => {
      openYamlTestFailureDetailModal(formatMessage, copy, fullText);
    },
  });
}
